import { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function AdminComunasPage() {
  const [comunas, setComunas] = useState([]);
  const [regiones, setRegiones] = useState([]);
  const [q, setQ] = useState("");
  const [regionFilter, setRegionFilter] = useState("ALL");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nombre: "", region: "" });
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      const [{ data: c }, { data: r }] = await Promise.all([
        api.get("/comunas"),
        api.get("/regiones"),
      ]);
      setComunas(c);
      setRegiones(r);
    } catch {
      toast.error("Error al cargar comunas");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    return comunas.filter((c) => {
      if (regionFilter !== "ALL" && String(c.region) !== regionFilter) return false;
      if (q && !c.nombre.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [comunas, q, regionFilter]);

  const openCreate = () => {
    setForm({ nombre: "", region: "" });
    setEditingId(null);
    setOpen(true);
  };
  const openEdit = (c) => {
    setForm({ nombre: c.nombre, region: String(c.region) });
    setEditingId(c.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.nombre || !form.region) {
      toast.error("Nombre y región son requeridos");
      return;
    }
    try {
      const payload = { nombre: form.nombre, region: parseInt(form.region, 10) };
      if (editingId) {
        await api.patch(`/comunas/${editingId}`, payload);
        toast.success("Comuna actualizada");
      } else {
        await api.post("/comunas", payload);
        toast.success("Comuna creada");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar comuna?")) return;
    try {
      await api.delete(`/comunas/${id}`);
      toast.success("Comuna eliminada");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-6 md:p-10">
        <PageHeader
          eyebrow="Administración"
          title="Comunas & Regiones"
          description="Mapa oficial de comuna → región de Chile utilizado para generar el archivo SAP."
          actions={
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button
                  onClick={openCreate}
                  data-testid="comuna-new-btn"
                  className="flex items-center gap-2 px-5 py-3 bg-[#D95A40] hover:bg-[#c94d33] text-white font-bold uppercase tracking-[0.15em] text-xs transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Nueva comuna
                </button>
              </DialogTrigger>
              <DialogContent className="bg-white">
                <DialogHeader>
                  <DialogTitle className="font-display text-xl tracking-tighter">
                    {editingId ? "Editar comuna" : "Nueva comuna"}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600 block mb-1.5">
                      Nombre
                    </label>
                    <input
                      value={form.nombre}
                      onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                      data-testid="comuna-form-nombre"
                      className="w-full px-3 py-2 border border-slate-300 text-sm focus:border-[#D95A40] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600 block mb-1.5">
                      Región
                    </label>
                    <Select
                      value={form.region}
                      onValueChange={(v) => setForm({ ...form, region: v })}
                    >
                      <SelectTrigger data-testid="comuna-form-region">
                        <SelectValue placeholder="Seleccionar región" />
                      </SelectTrigger>
                      <SelectContent className="bg-white max-h-72">
                        {regiones.map((r) => (
                          <SelectItem key={r.numero} value={String(r.numero)}>
                            {r.numero} · {r.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <button
                    onClick={() => setOpen(false)}
                    className="px-4 py-2 border border-slate-300 text-sm hover:bg-slate-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={save}
                    data-testid="comuna-form-save"
                    className="px-5 py-2 bg-[#D95A40] hover:bg-[#c94d33] text-white text-sm font-bold uppercase tracking-wider"
                  >
                    {editingId ? "Guardar" : "Crear"}
                  </button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          }
        />

        <div className="mb-4 flex flex-col md:flex-row gap-3">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar comuna..."
            data-testid="comuna-search"
            className="flex-1 px-3 py-2.5 bg-white border border-slate-300 text-sm focus:border-[#D95A40] focus:outline-none"
          />
          <div className="w-full md:w-64">
            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger data-testid="comuna-region-filter">
                <SelectValue placeholder="Todas las regiones" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value="ALL">Todas las regiones</SelectItem>
                {regiones.map((r) => (
                  <SelectItem key={r.numero} value={String(r.numero)}>
                    {r.numero} · {r.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0B1B3D] text-white">
              <tr>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.15em]">Comuna</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.15em]">Región</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.15em]">Nombre región</th>
                <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-[0.15em]">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50" data-testid={`comuna-row-${c.id}`}>
                  <td className="px-4 py-3 font-semibold text-[#0B1B3D]">{c.nombre}</td>
                  <td className="px-4 py-3 tabular-nums">{c.region}</td>
                  <td className="px-4 py-3 text-slate-700">{c.region_nombre}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        onClick={() => openEdit(c)}
                        data-testid={`comuna-edit-${c.id}`}
                        className="p-2 hover:bg-slate-100 text-slate-600"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => remove(c.id)}
                        data-testid={`comuna-delete-${c.id}`}
                        className="p-2 hover:bg-red-50 text-slate-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-slate-500">
                    Sin resultados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}
